import React, { createContext, useContext, useState, useCallback } from 'react';

const StagedEditContext = createContext();

/**
 * Context for managing items staged for editing in the Manage tab.
 * Items are sent here from the Staging Tray shelf when user wants to edit them.
 */
export function StagedEditProvider({ children }) {
  const [stagedEditItems, setStagedEditItemsState] = useState([]);

  // Set items for editing (replaces any existing)
  const setStagedEditItems = useCallback((items) => {
    setStagedEditItemsState(items);
  }, []);

  // Add items to existing staged items
  const addStagedEditItems = useCallback((items) => {
    setStagedEditItemsState(prev => {
      // Avoid duplicates by ID
      const existingIds = new Set(prev.map(item => item.id));
      const newItems = items.filter(item => !existingIds.has(item.id));
      return [...newItems, ...prev];
    });
  }, []);

  // Remove a single item
  const removeStagedEditItem = useCallback((itemId) => {
    setStagedEditItemsState(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // Clear all staged items
  const clearStagedEditItems = useCallback(() => {
    setStagedEditItemsState([]);
  }, []);

  const value = {
    stagedEditItems,
    setStagedEditItems,
    addStagedEditItems,
    removeStagedEditItem,
    clearStagedEditItems,
    hasStagedEditItems: stagedEditItems.length > 0
  };

  return (
    <StagedEditContext.Provider value={value}>
      {children}
    </StagedEditContext.Provider>
  );
}

export function useStagedEdit() {
  const context = useContext(StagedEditContext);
  if (!context) {
    throw new Error('useStagedEdit must be used within StagedEditProvider');
  }
  return context;
}

export default StagedEditContext;
